// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect } from 'react';
import { policyMetadataApi } from '../api/policyMetadataApi';
import { providerSubmissionApi } from '../api/providerSubmissionApi';
import type {
  PolicyStructuredDetails,
  PolicyCustomMetadata,
  PolicyMetadataEditHistory,
  PolicyMetadataExistsResponse,
  SaveStructuredDetailsRequest,
  AddCustomMetadataRequest,
  UpdateCustomMetadataRequest
} from '../types/policyMetadata';
import type { ProviderSubmissionDTO } from '../types/providerAccess';

interface UsePolicyMetadataReturn {
  // State
  structuredDetails: PolicyStructuredDetails | null;
  customMetadata: PolicyCustomMetadata[];
  editHistory: PolicyMetadataEditHistory[];
  metadataStatus: PolicyMetadataExistsResponse | null;
  latestProviderSubmission: ProviderSubmissionDTO | null;
  providerDocuments: ProviderSubmissionDTO[];
  loading: boolean;
  saving: boolean;
  error: string | null;

  // Structured Details Actions
  loadStructuredDetails: () => Promise<void>;
  saveStructuredDetails: (request: SaveStructuredDetailsRequest) => Promise<PolicyStructuredDetails>;
  saveStructuredDetailsOnBehalf: (advisorId: string, request: SaveStructuredDetailsRequest) => Promise<PolicyStructuredDetails>;

  // Custom Metadata Actions
  loadCustomMetadata: () => Promise<void>;
  addCustomMetadata: (request: AddCustomMetadataRequest) => Promise<PolicyCustomMetadata>;
  addCustomMetadataOnBehalf: (advisorId: string, request: AddCustomMetadataRequest) => Promise<PolicyCustomMetadata>;
  updateCustomMetadata: (metadataId: string, request: UpdateCustomMetadataRequest) => Promise<PolicyCustomMetadata>;
  updateCustomMetadataOnBehalf: (metadataId: string, advisorId: string, request: UpdateCustomMetadataRequest) => Promise<PolicyCustomMetadata>;
  deleteCustomMetadata: (metadataId: string, changeReason: string) => Promise<void>;
  deleteCustomMetadataOnBehalf: (metadataId: string, advisorId: string, changeReason: string) => Promise<void>;

  // Edit History Actions
  loadEditHistory: (options?: { fieldFilter?: string; startDate?: string; endDate?: string }) => Promise<void>;

  // Status Check
  checkMetadataStatus: () => Promise<void>;

  // Utility
  refresh: () => Promise<void>;
  clearError: () => void;
}

export function usePolicyMetadata(policyId: string): UsePolicyMetadataReturn {
  const [structuredDetails, setStructuredDetails] = useState<PolicyStructuredDetails | null>(null);
  const [customMetadata, setCustomMetadata] = useState<PolicyCustomMetadata[]>([]);
  const [editHistory, setEditHistory] = useState<PolicyMetadataEditHistory[]>([]);
  const [metadataStatus, setMetadataStatus] = useState<PolicyMetadataExistsResponse | null>(null);
  const [latestProviderSubmission, setLatestProviderSubmission] = useState<ProviderSubmissionDTO | null>(null);
  const [providerDocuments, setProviderDocuments] = useState<ProviderSubmissionDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // =========================================================================
  // Structured Details
  // =========================================================================

  const loadStructuredDetails = useCallback(async () => {
    if (!policyId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await policyMetadataApi.getStructuredDetails(policyId);
      setStructuredDetails(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to load structured details:', err);
      setError(error.response?.data?.message || 'Failed to load structured details');
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  const saveStructuredDetails = useCallback(async (
    request: SaveStructuredDetailsRequest
  ): Promise<PolicyStructuredDetails> => {
    setSaving(true);
    setError(null);

    try {
      const result = await policyMetadataApi.saveStructuredDetails(policyId, request);
      setStructuredDetails(result);
      return result;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to save structured details:', err);
      const errorMessage = error.response?.data?.message || 'Failed to save structured details';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [policyId]);

  const saveStructuredDetailsOnBehalf = useCallback(async (
    advisorId: string,
    request: SaveStructuredDetailsRequest
  ): Promise<PolicyStructuredDetails> => {
    setSaving(true);
    setError(null);

    try {
      const result = await policyMetadataApi.saveStructuredDetailsOnBehalf(policyId, advisorId, request);
      setStructuredDetails(result);
      return result;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to save structured details on behalf:', err);
      const errorMessage = error.response?.data?.message || 'Failed to save structured details';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [policyId]);

  // =========================================================================
  // Custom Metadata
  // =========================================================================

  const loadCustomMetadata = useCallback(async () => {
    if (!policyId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await policyMetadataApi.getCustomMetadata(policyId);
      setCustomMetadata(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to load custom metadata:', err);
      setError(error.response?.data?.message || 'Failed to load custom metadata');
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  const addCustomMetadata = useCallback(async (
    request: AddCustomMetadataRequest
  ): Promise<PolicyCustomMetadata> => {
    setSaving(true);
    setError(null);

    try {
      const result = await policyMetadataApi.addCustomMetadata(policyId, request);
      setCustomMetadata(prev => [...prev, result]);
      return result;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to add custom metadata:', err);
      const errorMessage = error.response?.data?.message || 'Failed to add custom metadata';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [policyId]);

  const addCustomMetadataOnBehalf = useCallback(async (
    advisorId: string,
    request: AddCustomMetadataRequest
  ): Promise<PolicyCustomMetadata> => {
    setSaving(true);
    setError(null);

    try {
      const result = await policyMetadataApi.addCustomMetadataOnBehalf(policyId, advisorId, request);
      setCustomMetadata(prev => [...prev, result]);
      return result;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to add custom metadata on behalf:', err);
      const errorMessage = error.response?.data?.message || 'Failed to add custom metadata';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [policyId]);

  const updateCustomMetadata = useCallback(async (
    metadataId: string,
    request: UpdateCustomMetadataRequest
  ): Promise<PolicyCustomMetadata> => {
    setSaving(true);
    setError(null);

    try {
      const result = await policyMetadataApi.updateCustomMetadata(policyId, metadataId, request);
      setCustomMetadata(prev => prev.map(item => item.id === metadataId ? result : item));
      return result;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to update custom metadata:', err);
      const errorMessage = error.response?.data?.message || 'Failed to update custom metadata';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [policyId]);

  const updateCustomMetadataOnBehalf = useCallback(async (
    metadataId: string,
    advisorId: string,
    request: UpdateCustomMetadataRequest
  ): Promise<PolicyCustomMetadata> => {
    setSaving(true);
    setError(null);

    try {
      const result = await policyMetadataApi.updateCustomMetadataOnBehalf(policyId, metadataId, advisorId, request);
      setCustomMetadata(prev => prev.map(item => item.id === metadataId ? result : item));
      return result;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to update custom metadata on behalf:', err);
      const errorMessage = error.response?.data?.message || 'Failed to update custom metadata';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [policyId]);

  const deleteCustomMetadata = useCallback(async (
    metadataId: string,
    changeReason: string
  ): Promise<void> => {
    setSaving(true);
    setError(null);

    try {
      await policyMetadataApi.deleteCustomMetadata(policyId, metadataId, changeReason);
      setCustomMetadata(prev => prev.filter(item => item.id !== metadataId));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to delete custom metadata:', err);
      const errorMessage = error.response?.data?.message || 'Failed to delete custom metadata';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [policyId]);

  const deleteCustomMetadataOnBehalf = useCallback(async (
    metadataId: string,
    advisorId: string,
    changeReason: string
  ): Promise<void> => {
    setSaving(true);
    setError(null);

    try {
      await policyMetadataApi.deleteCustomMetadataOnBehalf(policyId, metadataId, advisorId, changeReason);
      setCustomMetadata(prev => prev.filter(item => item.id !== metadataId));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to delete custom metadata on behalf:', err);
      const errorMessage = error.response?.data?.message || 'Failed to delete custom metadata';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [policyId]);

  // =========================================================================
  // Edit History
  // =========================================================================

  const loadEditHistory = useCallback(async (
    options?: { fieldFilter?: string; startDate?: string; endDate?: string }
  ) => {
    if (!policyId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await policyMetadataApi.getEditHistory(policyId, options);
      setEditHistory(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to load edit history:', err);
      setError(error.response?.data?.message || 'Failed to load edit history');
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  // =========================================================================
  // Status Check
  // =========================================================================

  const checkMetadataStatus = useCallback(async () => {
    if (!policyId) return;

    try {
      const data = await policyMetadataApi.checkMetadataExists(policyId);
      setMetadataStatus(data);
    } catch (err: unknown) {
      console.error('Failed to check metadata status:', err);
      // Don't set error for status check failures
    }
  }, [policyId]);

  // =========================================================================
  // Refresh All
  // =========================================================================

  const refresh = useCallback(async () => {
    if (!policyId) return;

    setLoading(true);
    setError(null);

    try {
      const [structured, custom, history, status, latestForm, allSubmissions] = await Promise.all([
        policyMetadataApi.getStructuredDetails(policyId),
        policyMetadataApi.getCustomMetadata(policyId),
        policyMetadataApi.getEditHistory(policyId),
        policyMetadataApi.checkMetadataExists(policyId),
        providerSubmissionApi.getLatestFormSubmission(policyId).catch(() => null),
        providerSubmissionApi.listSubmissions(policyId).catch(() => [] as ProviderSubmissionDTO[])
      ]);

      setStructuredDetails(structured);
      setCustomMetadata(custom);
      setEditHistory(history);
      setMetadataStatus(status);
      setLatestProviderSubmission(latestForm);
      setProviderDocuments(allSubmissions.filter(s => s.submissionType === 'DOCUMENT'));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to refresh metadata:', err);
      setError(error.response?.data?.message || 'Failed to load policy metadata');
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    // State
    structuredDetails,
    customMetadata,
    editHistory,
    metadataStatus,
    latestProviderSubmission,
    providerDocuments,
    loading,
    saving,
    error,

    // Structured Details Actions
    loadStructuredDetails,
    saveStructuredDetails,
    saveStructuredDetailsOnBehalf,

    // Custom Metadata Actions
    loadCustomMetadata,
    addCustomMetadata,
    addCustomMetadataOnBehalf,
    updateCustomMetadata,
    updateCustomMetadataOnBehalf,
    deleteCustomMetadata,
    deleteCustomMetadataOnBehalf,

    // Edit History Actions
    loadEditHistory,

    // Status Check
    checkMetadataStatus,

    // Utility
    refresh,
    clearError
  };
}
